import {
  Calendar,
  DotsThree,
  MapPin,
  Pencil,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react'
import { Dropdown, MenuProps } from 'antd'
import { infoMessage } from 'api/global'
import {
  useDeleteGameById,
  useGetLeagueGames,
} from 'api/league/leagueGames/leagueGames'
import CustomAvatar from 'components/global/CustomAvatar'
import SelectDateFromGivenDates from 'components/global/SelectDateFromGivenDates'
import Spinner from 'components/Spinner'
import AlertModal from 'helper/AlertModal'
import { convertMinutesToLocalTime } from 'helper/utility'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { leagueGamesStore } from 'state/league/leageGames'
import { leagueEventStore } from 'state/league/leagueEvent'
import {
  LeagueGameRespType,
  LeagueGamesByRoundDate,
} from 'types/league/leagueGame'

const Rounds: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({
    leagueData: store.leagueData,
  }))
  const {
    setAddGameModal,
    selectedGame,
    setSelectedGame,
    setEditTimeModal,
    setEditFieldModal,
  } = leagueGamesStore(store => ({
    setAddGameModal: store.setAddGameModal,
    selectedGame: store.selectedGame,
    setSelectedGame: store.setSelectedGame,
    setEditTimeModal: store.setEditTimeModal,
    setEditFieldModal: store.setEditFieldModal,
  }))
  const [params] = useState({
    leagueId: leagueData._id,
  })
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [alertModal, setAlertModal] = useState<boolean>(false)

  const {
    data: gamesByLeagueId,
    isLoading: gamesLoading,
    isFetching: gamesFetching,
    refetch: refetchGames,
  } = useGetLeagueGames(params)

  const { mutate: deleteGame, isLoading: deleteGameLoading } =
    useDeleteGameById()

  const handleYesDelete = () => {
    deleteGame(selectedGame._id, {
      onSuccess: () => setAlertModal(false),
      onError: () => {
        setAlertModal(false)
      },
    })
  }

  const dotOptions: MenuProps['items'] = [
    {
      key: 'edit-game',
      label: (
        <div className="flex align-center gap-16px">
          <Pencil size={20} color="#000" />
          <p>Edit game</p>
        </div>
      ),
    },
    {
      key: 'change-time',
      label: (
        <div className="flex align-center gap-16px">
          <Calendar size={20} color="#000" />
          <p>Edit time</p>
        </div>
      ),
    },
    {
      key: 'change-location',
      label: (
        <div className="flex align-center gap-16px">
          <MapPin size={20} color="#000" />
          <p>Change field</p>
        </div>
      ),
    },
    {
      key: 'delete-game',
      label: (
        <div className="flex align-center gap-16px">
          <Trash size={20} color="#FF6961" />
          <p>Delete game</p>
        </div>
      ),
    },
  ]

  const handleMenuClick = (key: string, game: LeagueGameRespType) => {
    switch (key) {
      case 'edit-game':
        setSelectedGame(game)
        setAddGameModal(true)
        break
      case 'change-time':
        setSelectedGame(game)
        setEditTimeModal(true)
        break
      case 'change-location':
        setSelectedGame(game)
        setEditFieldModal(true)
        break
      case 'delete-game':
        setSelectedGame(game)
        setAlertModal(true)
        break
      default:
        infoMessage('Something went wrong from fronted side rounds.tsx')
        break
    }
  }

  useEffect(() => {
    refetchGames()
  }, [params])

  return (
    <>
      <div className="rounds-container">
        <div className="calendar">
          <SelectDateFromGivenDates
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            availableDates={leagueData.datesOfEvent}
          />
        </div>
        <div className="rounds-main">
          {gamesLoading || gamesFetching ? (
            <Spinner />
          ) : (
            <>
              {gamesByLeagueId.length ? (
                <>
                  {gamesByLeagueId.map(
                    (gameRounds: LeagueGamesByRoundDate, index: number) => {
                      return (
                        <div className="round-box mt-10" key={index}>
                          <h3 className="text-14 w-600">
                            Round {gameRounds.round}
                          </h3>
                          {gameRounds.dates.map(gameDates => {
                            return (
                              <>
                                <div
                                  className="date-container"
                                  key={gameDates.date}
                                >
                                  <h4>
                                    {moment(gameDates.date).format(
                                      'dddd, MMMM DD',
                                    )}
                                  </h4>
                                  <PencilSimple
                                    size={20}
                                    color="#000"
                                    className="pointer"
                                  />
                                </div>
                                {gameDates.games.map(game => {
                                  return (
                                    <div className="game-box" key={game._id}>
                                      <div className="team-time-container">
                                        <div className="team">
                                          <CustomAvatar
                                            size={40}
                                            src={
                                              game.homeTeam.team.profilePhoto
                                                ?.thumb
                                            }
                                            name={game.homeTeam.team.name}
                                          />
                                          <b>{game.homeTeam.team.name}</b>
                                        </div>
                                        <div className="time">
                                          <span>{game.courtFieldNumber}</span>
                                          <b>
                                            {convertMinutesToLocalTime(
                                              game.timings.startTime,
                                            )}
                                          </b>
                                        </div>
                                        <div className="team">
                                          <CustomAvatar
                                            size={40}
                                            src={
                                              game.awayTeam.team.profilePhoto
                                                ?.thumb
                                            }
                                            name={game.awayTeam.team.name}
                                          />
                                          <b>{game.awayTeam.team.name}</b>
                                        </div>
                                      </div>
                                      <Dropdown
                                        menu={{
                                          items: dotOptions,
                                          onClick: ({ key }) =>
                                            handleMenuClick(String(key), game),
                                        }}
                                        trigger={['click']}
                                        placement="bottomRight"
                                        overlayStyle={{ minWidth: '150px' }}
                                      >
                                        <DotsThree
                                          size={32}
                                          color="#000"
                                          className="pointer"
                                        />
                                      </Dropdown>
                                    </div>
                                  )
                                })}
                              </>
                            )
                          })}
                        </div>
                      )
                    },
                  )}
                </>
              ) : (
                <div>No games created</div>
              )}
            </>
          )}
        </div>
      </div>

      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            heading: 'Delete game',
            message1: `Are you sure youn want to delete this game?`,
          }}
          onYesClick={handleYesDelete}
          loading={deleteGameLoading}
        />
      )}
    </>
  )
}

export default Rounds
